import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw, ChevronRight, Info, Settings, Check, Minus, Plus } from 'lucide-react';
import { PrimaryButton, SecondaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { CoRegulateMark } from '../components/Logo';
import { ToolFeedback } from '../components/ToolFeedback';

/**
 * Breathing Patterns - Multiple Breathing Exercises
 * 
 * Various scientifically-backed breathing patterns for different needs:
 * - Box Breathing (4-4-4-4) - Calm & Focus
 * - 4-7-8 Breathing - Sleep & Deep Relaxation
 * - Physiological Sigh - Quick Stress Relief
 * - Coherent Breathing (5-5) - HRV & Balance
 * - Extended Exhale (4-8) - Activation → Calm
 */

const BREATHING_PATTERNS = [
    {
        id: 'box',
        name: 'Box Breathing',
        subtitle: '4-4-4-4',
        description: 'Equal parts inhale, hold, exhale, hold. Used by Navy SEALs for calm under pressure.',
        bestFor: 'Focus, anxiety, stress',
        color: '#64B5F6',
        bgColor: '#E3F2FD',
        glassVariant: 'glass-card-blue',
        phases: [
            { name: 'Inhale', duration: 4, instruction: 'Breathe in slowly', grow: true },
            { name: 'Hold', duration: 4, instruction: 'Hold gently', grow: false },
            { name: 'Exhale', duration: 4, instruction: 'Release slowly', grow: false, shrink: true },
            { name: 'Hold', duration: 4, instruction: 'Rest here', grow: false }
        ]
    },
    {
        id: '478',
        name: '4-7-8 Breathing',
        subtitle: 'Relaxing Breath',
        description: 'Dr. Andrew Weil\'s technique. Extended hold and exhale activates parasympathetic response.',
        bestFor: 'Sleep, deep relaxation, panic',
        color: '#7D8E95',
        bgColor: '#EDE7F6',
        glassVariant: 'glass-card-lavender',
        phases: [
            { name: 'Inhale', duration: 4, instruction: 'Breathe in through nose', grow: true },
            { name: 'Hold', duration: 7, instruction: 'Hold your breath', grow: false },
            { name: 'Exhale', duration: 8, instruction: 'Exhale through mouth', grow: false, shrink: true }
        ]
    },
    {
        id: 'sigh',
        name: 'Physiological Sigh',
        subtitle: 'Quick Reset',
        description: 'Double inhale followed by long exhale. Instantly calms the nervous system.',
        bestFor: 'Immediate stress relief, 30 seconds',
        color: '#4DB6AC',
        bgColor: '#FEF6EF',
        glassVariant: 'glass-card-sage',
        phases: [
            { name: 'Inhale', duration: 2, instruction: 'Big breath in', grow: true },
            { name: 'Sip', duration: 1, instruction: 'One more sip of air', grow: true },
            { name: 'Exhale', duration: 6, instruction: 'Long slow exhale', grow: false, shrink: true }
        ]
    },
    {
        id: 'coherent',
        name: 'Coherent Breathing',
        subtitle: '5-5',
        description: '5 breaths per minute. Optimizes heart rate variability and autonomic balance.',
        bestFor: 'Heart health, emotional balance',
        color: '#F48FB1',
        bgColor: '#FCE4EC',
        glassVariant: 'glass-card-rose',
        phases: [
            { name: 'Inhale', duration: 6, instruction: 'Slow breath in', grow: true },
            { name: 'Exhale', duration: 6, instruction: 'Slow breath out', grow: false, shrink: true }
        ]
    },
    {
        id: 'extended',
        name: 'Extended Exhale',
        subtitle: '4-8',
        description: 'Exhale twice as long as inhale. Activates vagus nerve and calming response.',
        bestFor: 'Anxiety, racing heart, tension',
        color: '#81C784',
        bgColor: '#FEF6EF',
        glassVariant: 'glass-card-gold',
        phases: [
            { name: 'Inhale', duration: 4, instruction: 'Breathe in', grow: true },
            { name: 'Exhale', duration: 8, instruction: 'Slow exhale', grow: false, shrink: true }
        ]
    },
    {
        id: 'energizing',
        name: 'Energizing Breath',
        subtitle: '2-1-4-1',
        description: 'Quick inhale with holds to increase alertness and oxygen. Great for morning or afternoon slump.',
        bestFor: 'Energy, focus, alertness',
        color: '#FFB74D',
        bgColor: '#FFF3E0',
        glassVariant: 'glass-card-rose',
        phases: [
            { name: 'Inhale', duration: 2, instruction: 'Quick breath in', grow: true },
            { name: 'Hold', duration: 1, instruction: 'Brief hold', grow: false },
            { name: 'Exhale', duration: 4, instruction: 'Steady exhale', grow: false, shrink: true },
            { name: 'Hold', duration: 1, instruction: 'Brief pause', grow: false }
        ]
    },
    {
        id: 'ocean',
        name: 'Ocean Breath',
        subtitle: 'Ujjayi',
        description: 'Yogic breath with slight throat constriction. Creates a soothing ocean-like sound.',
        bestFor: 'Meditation, yoga, grounding',
        color: '#4FC3F7',
        bgColor: '#E1F5FE',
        glassVariant: 'glass-card-blue',
        phases: [
            { name: 'Inhale', duration: 5, instruction: 'Breathe in, throat slightly closed', grow: true },
            { name: 'Exhale', duration: 5, instruction: 'Ocean sound exhale', grow: false, shrink: true }
        ]
    },
    {
        id: 'triangle',
        name: 'Triangle Breathing',
        subtitle: '4-4-4',
        description: 'Three equal parts without the second hold. Simpler than box breathing.',
        bestFor: 'Beginners, quick calm',
        color: '#BA68C8',
        bgColor: '#F3E5F5',
        glassVariant: 'glass-card-lavender',
        phases: [
            { name: 'Inhale', duration: 4, instruction: 'Breathe in', grow: true },
            { name: 'Hold', duration: 4, instruction: 'Hold gently', grow: false },
            { name: 'Exhale', duration: 4, instruction: 'Release', grow: false, shrink: true }
        ]
    },
    {
        id: 'calm',
        name: 'Calming Breath',
        subtitle: '3-3-6-3',
        description: 'Gentle pattern with extended exhale. Perfect for winding down.',
        bestFor: 'Evening, pre-sleep, gentle calm',
        color: '#7986CB',
        bgColor: '#E8EAF6',
        glassVariant: 'glass-card-sage',
        phases: [
            { name: 'Inhale', duration: 3, instruction: 'Gentle breath in', grow: true },
            { name: 'Hold', duration: 3, instruction: 'Soft hold', grow: false },
            { name: 'Exhale', duration: 6, instruction: 'Long, slow release', grow: false, shrink: true },
            { name: 'Hold', duration: 3, instruction: 'Rest empty', grow: false }
        ]
    },
    {
        id: 'square',
        name: 'Square Breathing',
        subtitle: '5-5-5-5',
        description: 'Like box breathing but longer. Deeper relaxation for experienced practitioners.',
        bestFor: 'Deep meditation, advanced practice',
        color: '#90A4AE',
        bgColor: '#ECEFF1',
        glassVariant: 'glass-card-gold',
        phases: [
            { name: 'Inhale', duration: 5, instruction: 'Deep breath in', grow: true },
            { name: 'Hold', duration: 5, instruction: 'Hold comfortably', grow: false },
            { name: 'Exhale', duration: 5, instruction: 'Complete release', grow: false, shrink: true },
            { name: 'Hold', duration: 5, instruction: 'Empty stillness', grow: false }
        ]
    }
];

// Map URL patterns to pattern IDs
const URL_PATTERN_MAP = {
    'physiological-sigh': 'sigh',
    'sigh': 'sigh',
    '4-7-8': '478',
    '478': '478',
    'box': 'box',
    'extended-exhale': 'extended',
    'extended': 'extended',
    'coherent': 'coherent'
};

const BreathingPatterns = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [selectedPattern, setSelectedPattern] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const [currentPhase, setCurrentPhase] = useState(0);
    const [progress, setProgress] = useState(0);
    const [cycleCount, setCycleCount] = useState(0);
    const [showInfo, setShowInfo] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [completedCycles, setCompletedCycles] = useState(0);
    
    // Custom timing settings (stored per pattern)
    const [customTimings, setCustomTimings] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('coregulate_breathing_timings') || '{}');
        } catch {
            return {};
        }
    });

    // Save custom timings to localStorage
    const saveCustomTimings = (patternId, timings) => {
        const updated = { ...customTimings, [patternId]: timings };
        setCustomTimings(updated);
        localStorage.setItem('coregulate_breathing_timings', JSON.stringify(updated));
    };

    // Get current pattern with custom timings applied
    const getPatternWithCustomTimings = (patternId) => {
        const basePattern = BREATHING_PATTERNS.find(p => p.id === patternId);
        if (!basePattern) return null;
        
        const customTiming = customTimings[patternId];
        if (!customTiming) return basePattern;
        
        // Apply custom timings to phases
        return {
            ...basePattern,
            phases: basePattern.phases.map((phase, index) => ({
                ...phase,
                duration: customTiming[index] !== undefined ? customTiming[index] : phase.duration
            }))
        };
    };

    // Handle URL pattern parameter
    useEffect(() => {
        const urlPattern = searchParams.get('pattern');
        if (urlPattern) {
            const patternId = URL_PATTERN_MAP[urlPattern.toLowerCase()] || urlPattern;
            const exists = BREATHING_PATTERNS.find(p => p.id === patternId);
            if (exists) {
                setSelectedPattern(patternId);
            }
        }
    }, [searchParams]);

    const pattern = selectedPattern ? getPatternWithCustomTimings(selectedPattern) : null;
    const phase = pattern ? pattern.phases[currentPhase] : null;

    // Timer logic
    useEffect(() => {
        if (!isRunning || !phase) return;

        const phaseDuration = phase.duration * 1000;
        const startTime = Date.now();
        
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const phaseProgress = Math.min(elapsed / phaseDuration, 1);
            setProgress(phaseProgress);

            if (elapsed >= phaseDuration) {
                // Move to next phase
                setCurrentPhase(prev => {
                    const next = (prev + 1) % pattern.phases.length;
                    if (next === 0) {
                        setCycleCount(c => c + 1);
                        setCompletedCycles(c => {
                            const newCount = c + 1;
                            // Show feedback after 3+ cycles completed
                            if (newCount >= 3 && !showFeedback) {
                                // Don't show immediately, wait for user to pause or finish
                            }
                            return newCount;
                        });
                    }
                    return next;
                });
                setProgress(0);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [isRunning, currentPhase, phase, pattern, showFeedback]);

    const handleStart = () => {
        setIsRunning(true);
    };

    const handlePause = () => {
        setIsRunning(false);
        // Show feedback if user completed at least 3 cycles
        if (completedCycles >= 3) {
            setShowFeedback(true);
        }
    };

    const handleReset = () => {
        setIsRunning(false);
        setCurrentPhase(0);
        setProgress(0);
        setCycleCount(0);
    };

    const handleBack = () => {
        if (selectedPattern) {
            // Show feedback if user completed at least 2 cycles
            if (completedCycles >= 2 && !showFeedback) {
                setShowFeedback(true);
                return;
            }
            handleReset();
            setSelectedPattern(null);
            setCompletedCycles(0);
            setShowSettings(false);
        } else {
            navigate(-1);
        }
    };

    const handleFeedbackClose = () => {
        setShowFeedback(false);
        // If they were trying to go back, complete the action
        if (completedCycles >= 2) {
            handleReset();
            setSelectedPattern(null);
            setCompletedCycles(0);
            setShowSettings(false);
        }
    };

    // Timing adjustment handlers
    const adjustTiming = (phaseIndex, delta) => {
        const currentTimings = customTimings[selectedPattern] || 
            pattern.phases.map(p => p.duration);
        
        const newTimings = [...currentTimings];
        newTimings[phaseIndex] = Math.max(1, Math.min(15, (newTimings[phaseIndex] || pattern.phases[phaseIndex].duration) + delta));
        
        saveCustomTimings(selectedPattern, newTimings);
    };

    const resetTimings = () => {
        const updated = { ...customTimings };
        delete updated[selectedPattern];
        setCustomTimings(updated);
        localStorage.setItem('coregulate_breathing_timings', JSON.stringify(updated));
    };

    // Calculate visual scale
    const getScale = () => {
        if (!phase) return 1;
        
        if (phase.grow) {
            return 1 + (progress * 0.5);
        } else if (phase.shrink) {
            return 1.5 - (progress * 0.5);
        }
        return pattern.phases[currentPhase > 0 ? currentPhase - 1 : pattern.phases.length - 1].grow ? 1.5 : 1;
    };

    // Selection screen
    if (!selectedPattern) {
        return (
            <div className="min-h-screen bg-transparent">
                <header className="sticky top-0 z-40 glass-header">
                    <div className="max-w-md mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                            <IconButton onClick={() => navigate(-1)} data-testid="back-btn" className="text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#FFBB98]/40 transition-all duration-200 flex-shrink-0">
                                <ArrowLeft className="w-5 h-5" />
                            </IconButton>
                            <div className="min-w-0">
                                <Heading level={3} className="text-[#1D6B6B] text-base sm:text-lg">
                                    Breathing Patterns
                                </Heading>
                                <SmallText className="text-[#7D8E95] text-xs sm:text-sm">
                                    Different patterns for different needs
                                </SmallText>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/home')}
                            className="hover:opacity-80 transition-opacity flex-shrink-0"
                            data-testid="header-logo"
                            aria-label="Go to dashboard"
                        >
                            <CoRegulateMark size={28} />
                        </button>
                    </div>
                </header>

                <main className="max-w-md mx-auto px-4 py-6 space-y-4">
                    <BodyText className="text-[#7D8E95] px-2 mb-4">
                        Each breathing pattern has a different effect on your nervous system. 
                        Choose based on what you need right now.
                    </BodyText>

                    {BREATHING_PATTERNS.map((bp, index) => (
                        <button
                            key={bp.id}
                            onClick={() => setSelectedPattern(bp.id)}
                            className={`w-full ${bp.glassVariant || 'glass-card'} rounded-2xl p-5 text-left card-hover hover-shine brand-slide-up`}
                            style={{ 
                                animationDelay: `${index * 50}ms`
                            }}
                            data-testid={`pattern-${bp.id}`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <Heading level={4} className="text-[#1D6B6B]">
                                        {bp.name}
                                    </Heading>
                                    <SmallText style={{ color: bp.color }} className="font-medium">
                                        {bp.subtitle}
                                    </SmallText>
                                </div>
                                <div 
                                    className="w-10 h-10 rounded-full flex items-center justify-center shadow-soft-sm"
                                    style={{ backgroundColor: bp.color + '25', boxShadow: `0 4px 16px -4px ${bp.color}40` }}
                                >
                                    <ChevronRight className="w-5 h-5" style={{ color: bp.color }} />
                                </div>
                            </div>
                            <BodyText className="text-[#7D8E95] text-sm mb-2">
                                {bp.description}
                            </BodyText>
                            <SmallText className="text-[#7D8E95]">
                                Best for: {bp.bestFor}
                            </SmallText>
                        </button>
                    ))}
                </main>
            </div>
        );
    }

    // Exercise screen
    const scale = getScale();
    const totalCycleTime = pattern.phases.reduce((sum, p) => sum + p.duration, 0);
    const basePattern = BREATHING_PATTERNS.find(p => p.id === selectedPattern);
    const hasCustomTimings = customTimings[selectedPattern] !== undefined;

    return (
        <div className="min-h-screen" style={{ backgroundColor: pattern.bgColor }}>
            {/* Feedback Modal */}
            <ToolFeedback
                isOpen={showFeedback}
                onClose={handleFeedbackClose}
                toolId={`breathing-${selectedPattern}`}
                toolName={pattern.name}
            />

            <header className="sticky top-0 z-40 p-4 backdrop-blur-md" style={{ backgroundColor: pattern.bgColor + 'DD' }}>
                <div className="flex items-center justify-between">
                    <IconButton onClick={handleBack} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <div className="text-center">
                        <SmallText className="text-[#7D8E95] font-medium">{pattern.name}</SmallText>
                        <SmallText className="text-[#7D8E95]">
                            {hasCustomTimings ? 'Custom timing' : pattern.subtitle}
                        </SmallText>
                    </div>
                    <div className="flex gap-1">
                        <IconButton 
                            onClick={() => setShowSettings(!showSettings)} 
                            data-testid="settings-btn"
                            className={showSettings ? 'bg-white/50' : ''}
                        >
                            <Settings className="w-5 h-5" />
                        </IconButton>
                        <IconButton onClick={() => setShowInfo(!showInfo)} data-testid="info-btn">
                            <Info className="w-5 h-5" />
                        </IconButton>
                    </div>
                </div>
            </header>

            {/* Info panel */}
            {showInfo && (
                <div className="mx-4 mb-4 p-4 glass-card rounded-xl animate-in fade-in slide-in-from-top-2">
                    <BodyText className="text-[#2A8585] mb-2">{pattern.description}</BodyText>
                    <SmallText className="text-[#7D8E95]">Best for: {pattern.bestFor}</SmallText>
                </div>
            )}

            {/* Settings panel - Customization */}
            {showSettings && (
                <div className="mx-4 mb-4 p-4 glass-card rounded-xl animate-in fade-in slide-in-from-top-2" data-testid="timing-settings">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <SmallText className="text-[#1D6B6B] font-semibold">Customize Timing</SmallText>
                            <SmallText className="text-[#7D8E95] text-xs">Adjust seconds for each phase</SmallText>
                        </div>
                        {hasCustomTimings && (
                            <button 
                                onClick={resetTimings}
                                className="text-xs text-[#7D8E95] hover:text-[#1D6B6B] underline"
                                data-testid="reset-timings-btn"
                            >
                                Reset to default
                            </button>
                        )}
                    </div>
                    
                    <div className="space-y-3">
                        {pattern.phases.map((p, index) => {
                            const currentDuration = customTimings[selectedPattern]?.[index] ?? p.duration;
                            const defaultDuration = basePattern.phases[index].duration;
                            const isModified = currentDuration !== defaultDuration;
                            
                            return (
                                <div key={index} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <SmallText className="text-[#2A8585] font-medium w-16">{p.name}</SmallText>
                                        {isModified && (
                                            <span className="text-xs text-[#FFBB98]">(was {defaultDuration}s)</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => adjustTiming(index, -1)}
                                            disabled={currentDuration <= 1}
                                            className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            data-testid={`timing-decrease-${index}`}
                                        >
                                            <Minus className="w-4 h-4 text-[#7D8E95]" />
                                        </button>
                                        <span 
                                            className="w-10 text-center font-semibold"
                                            style={{ color: pattern.color }}
                                        >
                                            {currentDuration}s
                                        </span>
                                        <button
                                            onClick={() => adjustTiming(index, 1)}
                                            disabled={currentDuration >= 15}
                                            className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center hover:bg-white/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            data-testid={`timing-increase-${index}`}
                                        >
                                            <Plus className="w-4 h-4 text-[#7D8E95]" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    
                    <div className="mt-4 pt-3 border-t border-[#7D8E95]/20">
                        <SmallText className="text-[#7D8E95] text-center">
                            Total cycle: {totalCycleTime}s
                            {hasCustomTimings && (
                                <span className="text-[#FFBB98] ml-2">
                                    (default: {basePattern.phases.reduce((sum, p) => sum + p.duration, 0)}s)
                                </span>
                            )}
                        </SmallText>
                    </div>
                </div>
            )}

            <main className="flex flex-col items-center justify-center min-h-[70vh] px-6">
                {/* Breathing circle */}
                <div className="relative w-64 h-64 flex items-center justify-center mb-8">
                    {/* Outer ring */}
                    <div 
                        className="absolute inset-0 rounded-full border-4 transition-all duration-100"
                        style={{ 
                            borderColor: pattern.color + '40',
                            transform: `scale(${scale})`
                        }}
                    />
                    
                    {/* Inner circle */}
                    <div 
                        className="w-40 h-40 rounded-full flex items-center justify-center transition-all duration-100"
                        style={{ 
                            backgroundColor: pattern.color + '30',
                            transform: `scale(${scale})`
                        }}
                    >
                        <div className="text-center">
                            <Heading level={2} style={{ color: pattern.color }}>
                                {phase?.name}
                            </Heading>
                            <SmallText className="text-[#7D8E95] mt-1">
                                {Math.ceil(phase?.duration * (1 - progress))}s
                            </SmallText>
                        </div>
                    </div>

                    {/* Progress ring */}
                    <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                        <circle
                            cx="50"
                            cy="50"
                            r="45"
                            fill="none"
                            stroke={pattern.color}
                            strokeWidth="3"
                            strokeDasharray={`${progress * 283} 283`}
                            style={{ transform: `scale(${scale})`, transformOrigin: 'center' }}
                        />
                    </svg>
                </div>

                {/* Instruction */}
                <BodyText className="text-[#2A8585] text-center mb-2">
                    {phase?.instruction}
                </BodyText>

                {/* Cycle count */}
                <SmallText className="text-[#7D8E95] mb-8">
                    Cycle {cycleCount + 1} • {totalCycleTime}s per cycle
                </SmallText>

                {/* Controls */}
                <div className="flex justify-center gap-4">
                    {!isRunning ? (
                        <PrimaryButton 
                            onClick={handleStart} 
                            size="lg" 
                            data-testid="start-btn"
                            style={{ backgroundColor: pattern.color }}
                        >
                            <Play className="w-5 h-5 mr-2" />
                            Start
                        </PrimaryButton>
                    ) : (
                        <>
                            <IconButton onClick={handleReset} className="bg-white/50" data-testid="reset-btn">
                                <RotateCcw className="w-5 h-5" />
                            </IconButton>
                            <PrimaryButton 
                                onClick={handlePause} 
                                data-testid="pause-btn"
                                style={{ backgroundColor: pattern.color }}
                            >
                                <Pause className="w-5 h-5" />
                            </PrimaryButton>
                        </>
                    )}
                </div>

                {/* Phase indicators */}
                <div className="flex justify-center gap-3 mt-8">
                    {pattern.phases.map((p, index) => (
                        <div
                            key={index}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                                index === currentPhase 
                                    ? 'text-white' 
                                    : 'text-[#7D8E95] bg-white/50'
                            }`}
                            style={{ 
                                backgroundColor: index === currentPhase ? pattern.color : undefined 
                            }}
                        >
                            {p.name}
                        </div>
                    ))}
                </div>
            </main>
        </div>
    );
};

export default BreathingPatterns;
